import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../app/store';
import { LOADING_STATES, type LoadingState } from '../../types/redux';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import { adminAPI, type AdminAcces, type AdminTri, type AdminUsage, type AdminUsageParams } from '../../services/adminAPI';

/**
 * Administration (13/10/2026). `acces` est sondé une fois après la connexion
 * (`probeAdminAcces`, depuis les chargeurs initiaux des deux shells) : un 404 du
 * serveur vaut `non`, en silence — la section n'apparaît alors nulle part.
 */
export interface AdminState {
    acces: AdminAcces;
    usage: AdminUsage | null;
    status: LoadingState;
    error: string | null;
    tri: AdminTri;
}

export const initialAdminState: AdminState = {
    acces: 'inconnu',
    usage: null,
    status: LOADING_STATES.IDLE,
    error: null,
    tri: 'activite',
};

/** Lecture tolérante : les magasins de test n'ont pas toujours ce reducer. */
const stateOf = (s: RootState): AdminState => (s as Partial<RootState>).admin ?? initialAdminState;

export const probeAdminAcces = createAsyncThunk<Exclude<AdminAcces, 'inconnu'>, { instance: any }, { state: RootState; rejectValue: string }>(
    'admin/probeAcces',
    async ({ instance }, { rejectWithValue }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) return rejectWithValue('Impossible d\'obtenir le token');
            return await adminAPI.acces(token);
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Sonde indisponible');
        }
    },
    // Une seule sonde par session : on ne redemande pas une réponse déjà connue.
    { condition: (_arg, { getState }) => stateOf(getState()).acces === 'inconnu' },
);

export const loadAdminUsage = createAsyncThunk<AdminUsage, { instance: any } & AdminUsageParams, { state: RootState; rejectValue: string }>(
    'admin/loadUsage',
    async ({ instance, ...params }, { rejectWithValue }) => {
        try {
            const token = await getTokenRedirect(tokenRequest, instance);
            if (!token) return rejectWithValue('Impossible d\'obtenir le token');
            return await adminAPI.usage(token, params);
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Les chiffres ne peuvent pas être lus pour l\'instant.');
        }
    },
);

const adminSlice = createSlice({
    name: 'admin',
    initialState: initialAdminState,
    reducers: {
        setAdminTri(state, action: PayloadAction<AdminTri>) {
            state.tri = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(probeAdminAcces.fulfilled, (state, action) => {
                state.acces = action.payload;
            })
            // Une sonde en échec (réseau, jeton) laisse `inconnu` : elle pourra être rejouée.
            .addCase(loadAdminUsage.pending, (state, action) => {
                state.status = LOADING_STATES.LOADING;
                state.error = null;
                if (action.meta.arg.tri) state.tri = action.meta.arg.tri;
            })
            .addCase(loadAdminUsage.fulfilled, (state, action) => {
                state.status = LOADING_STATES.SUCCEEDED;
                state.usage = action.payload;
            })
            .addCase(loadAdminUsage.rejected, (state, action) => {
                state.status = LOADING_STATES.FAILED;
                state.error = action.payload ?? 'Les chiffres ne peuvent pas être lus pour l\'instant.';
            });
    },
});

export const { setAdminTri } = adminSlice.actions;

export const selectAdminAcces = (s: RootState): AdminAcces => stateOf(s).acces;
export const selectIsAdmin = (s: RootState): boolean => stateOf(s).acces === 'admin';
export const selectAdminUsage = (s: RootState): AdminUsage | null => stateOf(s).usage;
export const selectAdminStatus = (s: RootState): LoadingState => stateOf(s).status;
export const selectAdminError = (s: RootState): string | null => stateOf(s).error;
export const selectAdminTri = (s: RootState): AdminTri => stateOf(s).tri;

export default adminSlice.reducer;
